// Copyright (c) Hamilton Manufacturing Corp.
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { schemafy } from '../../_common/api/utility';
import Button from '../../_common/form/Button';
import Page from '../../_ecom/pageLayout/Page';
import Title from '../../_common/form/Title';
import Modal from '../../_common/Modal';
import Form from '../../_common/form/Form';
import Select from '../../_common/form/Select';
import Input from '../../_common/form/Input';
import { HamQuestGiver } from '../../_common/api/HamQuest';
import { QuestRoute } from '../../_common/api/QuestRoutes';
import { defaultSiteConfig, siteConfigVer } from '../../../InitialState';
import { useGlobalState } from '../../_common/globalState/GlobalStateProvider';
import Row from '../../_common/form/Row';
import Col from '../../_common/form/Col';
import Card from '../../_pwa/Card';
import { Navigate } from 'react-router-dom';
import Loading from '../../_common/Loading';
import versionData from '../../../version.json';

const Wrapper = styled(Col)`
    display: flex;
    flex-direction: column;
`
const JSONStyle = styled.pre`
    font-size: 0.5em;
`
const EcomCard = styled(Card)`
    background: ${({ theme }) => theme.body};
    color: ${({ theme }) => theme.text};
`

const SiteConfigUpdater = () => {
    const {
        state: { version },
    } = useGlobalState();

    const keys = {
        loggedIn: 'loggedIn',
        schemaVersion: 'schemaVersion',
        appVersion: 'appVersion',
        buildVersion: 'buildVersion',
        buildTimestamp: 'buildTimestamp'
    }

    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const [controller, setController] = useState({
        local: { 
            quest: new HamQuestGiver('https://localhost:7267/api/v1'),
            app_key: 'B92BAE9AE23244D0A5D8C1584F5356A4',
            url: 'https://localhost:7267/'
        },       
        dev: {
            quest: new HamQuestGiver(process.env.REACT_APP_API_DEV_URL),
            app_key: 'B92BAE9AE23244D0A5D8C1584F5356A4',
        },
        demo: {
            quest: new HamQuestGiver(process.env.REACT_APP_API_DEMO_URL),
            app_key: 'B92BAE9AE23244D0A5D8C1584F5356A4',
            url: 'https://hamilton.ewash-demo.net/',
        },
        production: {
            quest: new HamQuestGiver(process.env.REACT_APP_API_PROD_URL),
            app_key: '7CB8FA8C51424CA885043165B49C6CCE',
            url: 'https://hamilton.ewash.net/',
        }
    });

    controller.local.quest.replaceHeaderKey({app_key: controller.local.app_key});
    controller.dev.quest.replaceHeaderKey({ app_key: controller.dev.app_key });
    controller.demo.quest.replaceHeaderKey({ app_key: controller.demo.app_key });
    controller.production.quest.replaceHeaderKey({ app_key: controller.production.app_key });

    const updateController = (env, key, value) => {
        setController(e => ({
            ...e,
            [env]: {
                ...e[env],
                [key]: value || 'N/A',
            },
        }));
    };

    async function handleLogIn(env) {
        try {
            const { quest } = controller[env];
            const loginResponse = await quest.post(QuestRoute.user.login, {
                email: username,
                password: password,
                rememberMe: false,
            });
            quest.replaceHeaderKey({ authorization: 'Bearer ' + loginResponse.accessToken });
            updateController(env, keys.loggedIn, true);
            getNewVer(env);
        } catch (err) {
            alert('Failed to log in.');
        } finally {
            setSubmitting(false);
        }
    }

    const getNewVer = async (env) => {
        const { quest } = controller[env]
        const appVersion = await quest.get(QuestRoute.appVersion);
        updateController(env, keys.appVersion, appVersion);

        const siteConfig = await quest.get(QuestRoute.siteConfig);
        updateController(env, keys.schemaVersion, siteConfig.version);

        const buildVersion = await quest.get(QuestRoute.buildVersions);
        updateController(env, keys.buildVersion, buildVersion.version);
        updateController(env, keys.buildTimestamp, buildVersion.timeStamp);
    }

    const handleSubmit = async (event) => {
        event.preventDefault();
        setSubmitting(true);
        Object.keys(controller).map(async e => await handleLogIn(e));
    };

    const updateSiteConfig = (env) => {
        const { quest } = controller[env]
        if (window.confirm(`Are you sure you want to update the Site Config to version: ${siteConfigVer}`) === false) return;

        quest.put(QuestRoute.siteConfig, {
            siteConfig: schemafy(defaultSiteConfig, siteConfigVer)
        })
            .then(() => {
                getNewVer(env);
                alert(`Schema updates: ${env}`);
            })
            .catch(ex => {
                alert(ex);
            })

    }

    const updateAppVer = (env) => {
        const { quest } = controller[env]
        if (window.confirm(`Are you sure you want to update the App Version to version: ${version}`) === false) return;

        quest.put(QuestRoute.appVersion, {
            appVersion: version
        })
            .then(() => {
                getNewVer(env);
                alert(`App Ver updates: ${env}`);
            })
            .catch(ex => {
                alert(ex);
            })
    }

    const compareVersion = (ver1, ver2) => {
        if (!ver1 || !ver2) return;
        const toNum = val => parseInt(val.replace(/\./g, ''));
        return (ver1 === ver2 || toNum(ver1) < toNum(ver2)) ? true : false;
    }

    if (process.env.REACT_APP_ENV === 'prod') return <Navigate to="/" replace />;

    return (
        <Page>
            {
                (controller.dev.loggedIn || controller.demo.loggedIn || controller.production.loggedIn) ?
                    <>
                        <div>Build: {process.env.REACT_APP_BUILD_VERSION}</div>
                        <div>Time Stamp: {process.env.REACT_APP_BUILD_TIMESTAMP}</div>
                        <div>App: {version}</div>
                        <div>Schema: {siteConfigVer}</div>

                        <Row justify='space-between' align='flex-start' style={{ marginBottom: '2em' }}>
                            {
                                Object.entries(controller).map(([key, val], i) => (
                                    <Col key={`${i}-env`}>
                                        <Title>{key}</Title>
                                        {
                                            val.loggedIn ?
                                                <Col>
                                                    <span>Build: {val.buildVersion}</span>
                                                    <span>Timestamp: {val.buildTimestamp}</span>

                                                    <span>App: {val.appVersion}</span>
                                                    <button
                                                        onClick={() => updateAppVer(key)}
                                                        disabled={compareVersion(version, val.appVersion)}
                                                    >
                                                        {compareVersion(version, val.appVersion) ? 'Update unavailable' : `Update App version: ${version}`}
                                                    </button>

                                                    <span>Schema: {val.schemaVersion}</span>
                                                    <button
                                                        onClick={() => updateSiteConfig(key)}
                                                        disabled={compareVersion(siteConfigVer, val.schemaVersion)}
                                                    >
                                                        {compareVersion(siteConfigVer, val.schemaVersion) ? 'Update unavailable' : `Update Site Schema: ${siteConfigVer}`}
                                                    </button>

                                                </Col>
                                                : (
                                                    <Col>
                                                        <span>Logging In...</span>
                                                        <Loading />
                                                    </Col>
                                                )
                                        }
                                    </Col>
                                ))
                            }
                        </Row>

                        <EcomCard title='Configs' collapsible>
                            <JSONStyle>
                                {JSON.stringify(defaultSiteConfig, undefined, 4)}
                            </JSONStyle>
                        </EcomCard>

                        <EcomCard title='Schema' collapsible>
                            <JSONStyle>
                                {JSON.stringify(schemafy(defaultSiteConfig), undefined, 4)}
                            </JSONStyle>
                        </EcomCard>
                    </>
                    : <form onSubmit={handleSubmit}>
                        <h3>Log In</h3>
                        <div>
                            <input
                                type="text"
                                id="username"
                                name="username"
                                value={username}
                                placeholder='Username'
                                onChange={(e) => setUsername(e.target.value)}
                                disabled={submitting}
                            />
                        </div>
                        <div>
                            <input
                                type="password"
                                id="password"
                                name="password"
                                value={password}
                                placeholder='Password'
                                onChange={(e) => setPassword(e.target.value)}
                                disabled={submitting}
                            />
                        </div>
                        <button
                            type="submit"
                            disabled={submitting}
                        >Log In</button>
                        {submitting && <div>Logging In...</div>}
                    </form>
            }
        </Page>
    );
}

export default SiteConfigUpdater;